// assetView.mjs — 文档内资产呈现的纯逻辑（第五十二批，SB-042 约定：纯逻辑入 .mjs 配单测）。
//
// 病灶（需求方第五十二批反馈）：风格参考页的表格里图片尺寸各异，把单元格推挤得
// 无法阅读。根因是发布产物只是 `<img>`，浏览器按原始像素铺开——正确的解法不在
// markdown 里（那会污染源文件），而在**呈现层**：表格内的资产统一 1:1 留白缩略图，
// 点开在弹窗里按视口自适应，再叠加局部放大与透明区提示。
//
// 本模块只放**可判定、可测**的部分：种类判定、缩放/平移的数学与钳制、增强计划的
// 决策；DOM 操作与 React 组件留在调用侧。

// 发布端烤进的 class 契约（scripts/publish/asset-kind.mjs 是判定权威）。
export const ASSET_CLASS = 'doc-asset';
const KIND_PREFIX = 'doc-asset--';

// 从元素 className 串取种类；无 doc-asset 标记 ⇒ null（不是资产，别碰）。
export function kindFromClass(className) {
  const parts = String(className || '').split(/\s+/);
  if (!parts.includes(ASSET_CLASS)) return null;
  const hit = parts.find((c) => c.startsWith(KIND_PREFIX));
  return hit ? hit.slice(KIND_PREFIX.length) : 'file';
}

// 一个资产元素该被怎么增强。
//   tag:      'IMG' | 'A'
//   kind:     kindFromClass 的结果
//   inTable:  是否位于表格单元格内（决定是否套 1:1 缩略图框）
// 返回 { thumb, lightbox, upgrade } —— upgrade 为 'audio'|'video'|null。
// **第五十九批修**：`replace` 是本次新增的一位——它回答"升级出来的播放器是**插在**原
// 元素旁边，还是**顶替**它"。
//
// 病灶：音视频既可以写成 `[x](a.mp3)`（渲染成 <a>），也可以写成 `![x](a.mp3)`
// （渲染成 <img>）——asset-kind.mjs 的头注**明写**了后者要按扩展名判种类，也就是说
// 这条路径是设计里预期存在的。但 DOM 那侧只写了 `el.tagName === 'A'` 一个分支，
// 于是图片语法写的音频渲染成一个 src 指向 .mp3 的 <img>：浏览器画一个碎图标，
// 播放器永不出现，而 class 上明明白白写着 doc-asset--audio。
// 第五十八批我给风格参考页 §5 写音频时正是用了 `![]()`，把这个洞踩实了。
//
// 两种 tag 的收尾不同，所以必须分开：
//   <a>  原链接是有价值的下载退路 ⇒ 播放器**插在它后面**，链接留着；
//   <img> 那个 <img> 本身是坏的（浏览器在渲染一个不是图片的东西）⇒ 播放器**顶替**它。
export function enhancePlan({ tag, kind, inTable }) {
  if (!kind) return { thumb: false, lightbox: false, upgrade: null, replace: false };
  if (kind === 'image') {
    // 表格内才套方框：正文里的插图保持原有流式尺寸，避免把示意图压成方块。
    return { thumb: Boolean(inTable), lightbox: true, upgrade: null, replace: false };
  }
  if (kind === 'audio' || kind === 'video') {
    // markdown 里音视频可写成链接或图片语法；两种都要升级为播放器。
    // 视频另可进弹窗看大画面——但只有 <a> 形态才有可点的链接，<img> 形态被顶替掉了。
    const isImg = String(tag).toUpperCase() === 'IMG';
    return { thumb: false, lightbox: kind === 'video' && !isImg, upgrade: kind, replace: isImg };
  }
  return { thumb: false, lightbox: false, upgrade: null, replace: false };   // file：保持普通链接
}

// ── 写着「下载」的链接要真的下载（第二百六十九批）──────────────────────────
// 病灶：资产对账表在预览格里给每件在库的字节挂了 `[↓ 下载](…)`——需求方要的是
// 「不能预览的格式也起码有个下载按钮」。可 /api/docs/asset 按扩展名回正确的
// Content-Type、不带 Content-Disposition（inline 语义是 <img>/<audio>/<video> 要的，
// 同一条 URL 既喂播放器也喂链接），于是 .json/.css 一点开就在当前标签页里**打开**
// 而不是保存：页面上写着「下载」的东西不下载。
// 修在呈现层而不是端点：同源链接上的 download 属性正是「这一次点击要保存」的开关，
// 端点契约一个字不动。文件名不能省——href 的最后一个路径段恒为 `asset`，空值的
// download 会把每个文件都存成「asset」，所以从 `path` 查询参数取真实文件名。
// 哪些 <a> 加：file（唯一的表现形式就是链接，本来就该是下载）、audio（播放器插在
// 它后面、链接是下载退路）。**image 的 <a> 刻意不加**：固定测试集页把每张照片写成
// `[文件名](…jpg)`，读者点它是要看那张照片；改成强制保存会牵动那一页，是否要牵动
// 由需求方定，不在这里顺手改。video 的 <a> 是弹窗入口（onBodyClick 拦截），不加。
// <img> 不是链接，无此属性。
export function wantsDownloadAttr({ tag, kind }) {
  if (String(tag || '').toUpperCase() !== 'A' || !kind) return false;
  return kind === 'file' || kind === 'audio';
}

// 从资产端点的 href（`/api/docs/asset?group=…&path=…`）取要存成的文件名：`path`
// 的最后一段。取不到（不是资产端点形态）就回空串——空 download 仍会下载、只是
// 文件名退化，比不加属性（不下载）好。
export function downloadNameOf(href) {
  const s = String(href || '');
  const q = s.indexOf('?');
  if (q < 0) return '';
  let p = '';
  try { p = new URLSearchParams(s.slice(q + 1)).get('path') || ''; } catch { return ''; }
  return p.split('/').filter(Boolean).pop() || '';
}

// ── 缩放 ────────────────────────────────────────────────────────────────────
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 8;
export const ZOOM_STEP = 1.35;      // 一档滚轮/一次双击的倍率

export function clampZoom(z) {
  if (!Number.isFinite(z)) return ZOOM_MIN;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
}

// direction: >0 放大，<0 缩小。
export function nextZoom(current, direction) {
  return clampZoom(direction > 0 ? current * ZOOM_STEP : current / ZOOM_STEP);
}

// 平移钳制：放大后可平移的范围是"超出视口的那一半"。zoom<=1 时恒回原点——
// 这条保证了"一键还原"之后不会残留偏移（否则图会停在视口外，看起来像坏了）。
export function clampPan({ x, y }, { zoom, viewW, viewH }) {
  if (!(zoom > 1)) return { x: 0, y: 0 };
  const maxX = Math.max(0, (viewW * zoom - viewW) / 2);
  const maxY = Math.max(0, (viewH * zoom - viewH) / 2);
  const fin = (v) => (Number.isFinite(v) ? v : 0);
  return {
    x: Math.min(maxX, Math.max(-maxX, fin(x))),
    y: Math.min(maxY, Math.max(-maxY, fin(y))),
  };
}

// 以光标为锚点缩放：保持光标下的那一点不动（否则放大总是往中心跑，找细节很难）。
//   origin: {x,y} 光标相对视口中心的偏移
export function zoomAround({ pan, zoom, nextZoomValue, origin }) {
  const ratio = nextZoomValue / zoom;
  return {
    zoom: nextZoomValue,
    pan: { x: origin.x - (origin.x - pan.x) * ratio, y: origin.y - (origin.y - pan.y) * ratio },
  };
}

export const IDENTITY_VIEW = { zoom: ZOOM_MIN, pan: { x: 0, y: 0 } };
export const isIdentityView = (v) =>
  v.zoom === ZOOM_MIN && v.pan.x === 0 && v.pan.y === 0;

// ── 双指捏合 ────────────────────────────────────────────────────────────────
// 病灶（第五十六批 S2）：弹窗把 touchAction 设成 'none' 却只实现了滚轮缩放，
// 而滚轮在触屏上永不触发——手机上文档里的图**根本无法放大**，唯二的 ±按钮还被
// S1 的不换行头部挤出了屏幕。
//
// 为什么不是"放开 touchAction、还给浏览器原生捏合"：原生捏合缩放的是**页面视觉
// 视口**，放大的是整块 UI（含标题栏与按钮），且捏完停在放大态、需要再捏回去；
// 更要命的是那样一来 zoom% 标签与「还原」按钮全成了谎话（它们只知道自己那套
// transform）。既然已经有一套有单测的 zoom/pan 模型，就把两指手势喂给它，
// 三种输入（滚轮 / 双击 / 捏合）落到同一个状态上。touchAction:'none' 因此必须
// 保留——它正是"浏览器别抢这个手势"的开关。
//
// 坐标约定与 zoomAround 一致：点位是**相对视口中心**的偏移（调用侧负责换算）。
export function gestureOf(points) {
  const [a, b] = Array.isArray(points) ? points : [];
  if (!a || !b) return { dist: 0, center: { x: 0, y: 0 } };
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return { dist: Math.hypot(dx, dy), center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
}

// 一次捏合的当前视图 = 以**起手中点**为锚按指距比例缩放，再叠加中点自身的位移
// （两指一起挪 = 平移，与缩放同时发生，这是原生捏合的手感）。
// 全程以"起手快照"为基准而非逐帧累积：累积会把每帧的钳制误差滚雪球，手指回到
// 原位时图却回不去，看起来像漂移。
export function pinchView({ start, current, startView, viewW, viewH }) {
  const base = startView && startView.pan ? startView : IDENTITY_VIEW;
  // 两指重合（dist=0）时比例无定义——原样返回，别让 Infinity/NaN 进 state。
  if (!start || !current || !(start.dist > 0)) return base;
  const zoom = clampZoom(base.zoom * (current.dist / start.dist));
  const eff = zoom / base.zoom;                 // 钳制后**真正生效**的倍率
  const o = start.center;
  const pan = {
    x: o.x - (o.x - base.pan.x) * eff + (current.center.x - o.x),
    y: o.y - (o.y - base.pan.y) * eff + (current.center.y - o.y),
  };
  return { zoom, pan: clampPan(pan, { zoom, viewW, viewH }) };
}

// ── 捏合的"指对身份"（D-A） ─────────────────────────────────────────────────
// 病灶：起手快照（start / startView）只在 pointerdown 拍一次，而"当前手势"每一帧
// 都从活跃指针 Map 的**前两个**重新取。两者之间没有任何东西保证它们说的是同一对
// 手指。手机上按住三根（捏合的两根 + 搭在玻璃上的拇指或掌根，`size >= 2` 的写法
// 也说明作者本就把多指算在射程内）时抬走前两根之一，指针数仍是 2，于是既有的
// 「跌破两指就收尾」修复够不着；下一帧 pinchView 便拿**另一对**的指距去除以
// **这一对**的起手指距、再叠加两个互不相干的中点之差。
// 实测（360×600 的盒子、zoom 2、p1(100,300) p2(260,300) p3(300,500) 三指按下后
// 抬走 p1、p3 只挪 1 个像素）：zoom 从 2 跳到 2.561768188185652（+28%）、
// pan 从原点跳到 {x:100, y:100.5}（约 141px）——一像素的动作换来一次瞬移。
//
// 判据：起手快照必须**连同它属于哪一对手指**一起记，指对成员一变就地重新起手
// （start 取当前手势、startView 取当前视图 ⇒ 那一帧恰好是恒等变换，画面不动）。
// 只判"根数"是不够的：3→2 根数会变，2→2 换人根数不变，而后者才是这个病灶。
//
// 指对身份与顺序无关：gestureOf 的 dist/center 本来就对调两指不变，若这里让顺序
// 参与身份，浏览器换个投递次序就会白白重新起手（表现为捏合中途"卡一下"）。
export function pinchPairKey(ids) {
  const pair = Array.isArray(ids) ? ids.slice(0, 2) : [];
  if (pair.length < 2) return null;
  if (pair.some((id) => id === undefined || id === null)) return null;
  return pair.map(String).sort().join('|');
}

// 每一帧都过一遍：prev 是上一帧的快照，ids 是当前手势指对的 pointerId。
// 返回 null = 此刻不成捏合（调用侧据此清掉 pinchRef）。
export function syncPinch({ prev, ids, gesture, view }) {
  const pairKey = pinchPairKey(ids);
  if (!pairKey) return null;                              // 不足两指 ⇒ 捏合结束
  if (prev && prev.pairKey === pairKey) return prev;      // 还是原来那一对 ⇒ 沿用起手快照
  // 两指重合时 dist=0，拿它当基准会让 pinchView 从此原样返回（画面冻住）；
  // 不快照、等下一帧手指分开再起手。
  if (!gesture || !(gesture.dist > 0)) return null;
  return { pairKey, start: gesture, startView: view && view.pan ? view : IDENTITY_VIEW };
}

// ── 滚轮缩放的归属（D-C） ───────────────────────────────────────────────────
// 病灶：组件在 React 的 onWheel 里调 e.preventDefault()。React 18 把 wheel 注册成
// 根节点上的**被动**监听（react-dom 18.3.1 development 版 addTrappedEventListener
// 里 touchstart/touchmove/wheel 三者 isPassiveListener = true），被动监听里的
// preventDefault 永远无效：控制台每滚一格报一次 "Unable to preventDefault inside
// passive event listener invocation"，而触控板捏合是以 ctrl+wheel 到达的 ——
// 浏览器于是在弹窗底下把**整个页面**也缩放了一遍。
// 修法在调用侧：用 ref + addEventListener(..., { passive: false }) 自己挂一条
// 非被动的 wheel 监听。这里只放"这一格滚轮归谁"的判据。
//
// 为什么不是"图片态一律吃掉滚轮"：ctrl+滚轮缩放页面是无障碍能力。图片态我们给了
// 等价替代（同一格滚轮变成图的缩放，zoom% 与「还原」都跟着走），拿走才不算删能力；
// 视频等没有自有缩放模型的种类则一律放行，让浏览器该缩就缩。
export function wheelZoomIntent({ kind, deltaY } = {}) {
  if (kind !== 'image') return { handled: false, direction: 0 };
  const dy = Number(deltaY);
  // deltaY 为 0 = 纯横向滚（触控板两指左右划）：旧式 `deltaY < 0 ? +1 : -1` 会把它
  // 判成"缩小"，读者左右划一下图就莫名其妙小一档。不是我们的手势就别接。
  if (!Number.isFinite(dy) || dy === 0) return { handled: false, direction: 0 };
  return { handled: true, direction: dy < 0 ? +1 : -1 };
}

// ── 透明区提示的条纹（D-B） ─────────────────────────────────────────────────
// 病灶：弹窗给 <img> 挂的是 `dc-alpha-hint`，而全仓（除构建产物外）只有那一行提到
// 它——**没有任何 CSS 规则**。真正画条纹的是 DocBrowserPage 正文那条
// `&.dc-hint-on img.doc-asset--image`，可弹窗的 <img> 渲染进 MUI Dialog 的 portal，
// 既不在 .dc-hint-on 的子树里、也不带 doc-asset--image 类。于是一颗写着"透明区
// 提示：开"的按钮宣布了一个永远不会发生的状态，偏偏就在设计师**专门打开来判断
// alpha** 的那块面板上。
// 修法是让这颗按钮名副其实，而条纹只留一份定义：本函数出样式，两处挂载点各自
// 传自己的选择器。复制粘贴一份到弹窗里也能"跑起来"，但两份颜色/周期迟早会漂。
//
// 画法沿用正文那版（自 asset_store_client 移植时已带的三条修正：认 reduced-motion、
// 跟随主题配色、一条规则而不是三份拷贝）。第四条（挪到合成器友好的 ::before）依旧
// **故意不取**：<img> 挂不住可靠的伪元素，而把条纹放到外框会连信箱黑边一起画，
// 读起来像"整个画框都是透明的"，比那点重绘开销更糟——何况动画只在提示开着时跑。
export const ALPHA_HINT_ANIMATION = 'dcAlphaScroll';
const ALPHA_HINT_BAND_PX = 20;                                   // 单条斜纹宽
const ALPHA_HINT_CYCLE_PX = ALPHA_HINT_BAND_PX * 2;              // 一明一暗为一个周期
// 40px 周期沿 45° 铺开需要 40·√2 ≈ 56 的方块，边界才对得齐；动画每 2s 正好平移
// 一个方块宽，故循环无缝（关键帧里的 -56px 与这里必须同源，别各写各的）。
export const ALPHA_HINT_TILE_PX = Math.floor(ALPHA_HINT_CYCLE_PX * Math.SQRT2);

// target：条纹要落在哪个元素上，相对于挂 sx 的那个元素。
//   正文  —— '&.dc-hint-on img.doc-asset--image'（提示开关在祖先上）
//   弹窗  —— '&'（就是这张 <img> 自己，开关由 React 决定挂不挂这份 sx）
export function alphaHintSx(mode, target = '&') {
  const dark = mode === 'dark';
  const lo = dark ? '#3a3a3a' : '#d8d8d8';
  const hi = dark ? '#161616' : '#9e9e9e';
  return {
    [target]: {
      // 画在位图**背后**：只有 alpha 为 0 的地方才透出来，滚动让"透明"一望即知，
      // 而不会被误读成画面本身的一部分。
      backgroundImage: `repeating-linear-gradient(45deg, ${lo} 0px, ${lo} ${ALPHA_HINT_BAND_PX}px, ${hi} ${ALPHA_HINT_BAND_PX}px, ${hi} ${ALPHA_HINT_CYCLE_PX}px)`,
      backgroundSize: `${ALPHA_HINT_TILE_PX}px ${ALPHA_HINT_TILE_PX}px`,
      animation: `${ALPHA_HINT_ANIMATION} 2s linear infinite`,
    },
    [`@keyframes ${ALPHA_HINT_ANIMATION}`]: {
      from: { backgroundPosition: '0 0' },
      to: { backgroundPosition: `-${ALPHA_HINT_TILE_PX}px 0` },
    },
    // 停的是滚动，不是提示：条纹照画，只是不再动。
    '@media (prefers-reduced-motion: reduce)': {
      [target]: { animation: 'none' },
    },
  };
}

// ── 弹窗头部的布局不变式 ────────────────────────────────────────────────────
// 病灶（第五十六批 S1）：头部是一行 direction="row" 且**没有 flexWrap**，图片态
// 挂了 8 个孩子——zoom% 48 + 两个图标钮 + 四个 MUI 文本钮（各 minWidth 64）——
// 标题之前的固定下限就已约 460px，而手机视口 360~390px。行不换、祖先不裁，
// 排在最后的「关闭」被顶出屏幕；全屏 Dialog 又没有可点的遮罩、手机没有 Esc 键，
// 于是点开任意一张图就只能刷新页面才能出来。
//
// 这里导出而不是就地写在 sx 里，是为了让"会被顺手改回去"的两条约束留下判据
// （房屋风格同 docHistory.mjs 的 DOC_HEADER_META_PROPS）：
//   1) flexWrap: 'wrap' —— 工具条挤不下必须换行；
//   2) 不得出现 flexShrink: 0 —— 它会让 flexWrap 变成死条款（flex 基准取
//      max-content，即"全挤一行"的宽度，不许收缩就永远轮不到换行）。
// 「关闭」不在这一栏里：它被钉在标题那一行且 flexShrink: 0，那一行只有两个孩子，
// 任何宽度下都在屏内——逃生按钮不参与"挤不下就换行"的博弈。
export const LIGHTBOX_TOOLS_PROPS = {
  direction: 'row',
  spacing: 0.5,
  useFlexGap: true,
  // alignItems 必须在 sx 里：MUI v6 起 Stack 不收 system props（DP-17）——写在
  // 对象顶层经 {...spread} 传入与写成 JSX 属性同罪，且 mui-system-props 判据
  // 只扫得到 JSX 属性形态，这里靠测试断言钉住。
  sx: { alignItems: 'center', flexWrap: 'wrap', mt: 0.5, rowGap: 0.5 },
};

// ── 返回键即"关闭弹窗" ──────────────────────────────────────────────────────
// 逃生通道不能只有一颗按钮。全屏 Dialog 没有可点遮罩，手机没有 Esc；弹窗的开关
// 又是页面局部 state，于是读者的直觉动作（按返回）会把人带离 /docs 而不是关掉图。
// 做法：打开时压入一条**同 URL 的哨兵历史记录**，返回键先消耗它。
//
// 判据落在这里的原因是它有两个反直觉的正确写法，第一版两条都写错了（D5）：
//
// (a) 哨兵必须**有身份**，不能只判形状。history.back() 只是把游标往回挪，被吃掉的
//     那条哨兵仍以"前进项"活着；读者随后一次前进手势（安卓前进手势 / iOS 边缘右
//     滑 / Alt+→ / 鼠标侧键）就会停在这条弹窗已关的孤儿哨兵上。若判据只看形状，
//     下一次开图后按返回落到这条孤儿上会被判成"我们自己的收尾"⇒ **弹窗不关**，
//     而且每前进一次就多囤一条——正好把本功能要消灭的 S1 死局（点开图出不来）
//     换个形状请了回来。所以每次打开发一枚**唯一令牌**：只有落点带着**本次**的
//     令牌才算自己的收尾，别人的（含自己上一次的）孤儿一律判为"用户按了返回"。
//     令牌带随机段而非纯自增：历史记录能跨刷新存活（bfcache / 会话恢复），纯自增
//     会在重载后从头发号，和刷新前的孤儿撞上。
//
// (b) 压入的 state 必须**摊平当前 state**。react-router 把自己的记账放在
//     state.idx 上，我们压一条裸对象会让它的 getIndex() 读到 undefined，之后
//     每一次 push 都算出 idx: NaN（NaN + 1 仍是 NaN），从此整条历史的记账全废。
//
// 收尾同理：只有当**当前站在自己那条**哨兵上时才 back()（否则读者按关闭就会被
// 多送走一步，直接离开 /docs）。
//
// 令牌换掉了旧注释里"落在哨兵上也可能是 StrictMode 双挂载的回退"这条豁免，代价是
// 一条挂载纪律：**AssetLightbox 必须常驻挂载、只切 open**（DocBrowserPage 现在就是
// 这么用的）。StrictMode 的重复 effect 只发生在首次挂载，那时 open=false、这段
// effect 直接返回，压根不压哨兵；若哪天改成"open 时才挂组件"，双挂载就会先压一条
// 哨兵再回退，回退落点带的是上一枚令牌 ⇒ 图刚点开就自己关上。改挂载方式前先看这里。
export const BACK_SENTINEL = 'dcLightboxOpen';

// 每个文档生命周期一枚 nonce + 自增序号：同一次加载内绝不重号，跨加载也不撞。
const BACK_TOKEN_NONCE = Math.random().toString(36).slice(2, 10);
let backTokenSeq = 0;
export function makeBackToken() {
  backTokenSeq += 1;
  return `${BACK_SENTINEL}#${BACK_TOKEN_NONCE}-${backTokenSeq}`;
}

// 要压入的 history state：摊平当前 state（见上 (b)）再盖上本次令牌。
export function backSentinelState(currentState, token) {
  const base = currentState && typeof currentState === 'object' ? currentState : {};
  return { ...base, [BACK_SENTINEL]: token };
}

// 落点是不是**本次**打开压的那条哨兵。token 缺失时恒为 false（宁可多关一次，
// 也不要出现"返回键没反应"）。
export const isOurSentinel = (state, token) =>
  Boolean(token) && Boolean(state) && typeof state === 'object' && state[BACK_SENTINEL] === token;

// popstate：落点不是自己的哨兵 ⇒ 读者真的按了返回 ⇒ 关弹窗。
export const shouldCloseOnPop = (state, token) => !isOurSentinel(state, token);

// 卸载/关闭收尾：只有还站在自己那条哨兵上才把它吃掉。
export const shouldRewindOnUnmount = (state, token) => isOurSentinel(state, token);

// ── 点一下 vs 划一下 ────────────────────────────────────────────────────────
// 病灶（D6）：movedRef 原先只在"已放大后的单指拖拽"和"两指捏合"两条分支里置位，
// 而这两条分支分别要求 zoom > 1 与两根手指——100% 时单指划过留白区一次都不置位。
// 又因为 touchAction:'none' 掐掉了浏览器滚动，这一划最终以 pointerdown→pointerup
// 落在同一个盒子上收场，浏览器照样合成 click，onBackdropClick 便把它当成"点了
// 空白"而关掉弹窗——读者只是想滑一下看看，图就没了。
// 判据：位移超过手抖容差就不再是"点"，**与缩放级别无关**。
export const TAP_SLOP_PX = 8;      // 触屏手抖约 5px；取与 Chrome click slop 同量级
export function exceedsTapSlop(a, b, slop = TAP_SLOP_PX) {
  if (!a || !b) return false;
  const dx = Number(a.x) - Number(b.x);
  const dy = Number(a.y) - Number(b.y);
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return false;   // 缺坐标不算"动过"
  return Math.hypot(dx, dy) > slop;
}
